/**
 * src/pages/public/FlashGallery.jsx — the public flash gallery.
 *
 * A grid of available designs, each claimable with a deposit. The API only
 * returns available designs, so anything that disappears between page loads
 * has gone; the greyed-out "SOLD" treatment exists for designs that go while
 * the page is open, after a failed claim.
 *
 * Claiming takes a name and email and redirects to Stripe. Same rule as
 * booking: nothing is reserved until the deposit clears.
 *
 * REDESIGN.md Phase 4: restyled onto Stencil's tokens and components/ui/ —
 * no change to what this page fetches, sends, or when.
 */
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { api } from '../../lib/api.js';
import { useApi } from '../../lib/useApi.js';
import { formatMoney } from '../../lib/format.js';
import { studioSlugFromHostname } from '../../lib/tenancy.js';
import { Empty, ErrorBanner, Loader } from '../../components/ui.jsx';
import { Dialog } from '../../components/ui/Dialog.jsx';
import { Button, Input, PortalHeader } from '../../components/ui/index.js';
import PaymentForm from '../../components/PaymentForm.jsx';
import PortalFooter from './PortalFooter.jsx';
import { applyStudioAccent, clearStudioAccent } from '../../lib/studio-accent.js';
import './portal.css';

// Matches the exact message requirePayoutsReady() throws in
// api/src/routes/bookings.js — the only way to tell this 409 apart from the
// "already claimed" one below, since ApiError carries no error code.
const PAYOUTS_NOT_READY_MESSAGE = /not yet set up to take online deposits/;

export default function FlashGallery() {
  // The legacy `:slug`-in-path route param, or — once this studio has its
  // own subdomain (TS-28) — the slug implied by the page's own hostname.
  const { slug: pathSlug } = useParams();
  const slug = pathSlug ?? studioSlugFromHostname(window.location.hostname, import.meta.env.VITE_APP_BASE_DOMAIN);
  const [params] = useSearchParams();

  const studio = useApi(`/api/public/${slug}`);
  const gallery = useApi(`/api/public/${slug}/flash`);
  const [claiming, setClaiming] = useState(null);
  const [goneIds, setGoneIds] = useState([]);

  const branding = studio.data?.studio;

  useEffect(() => {
    applyStudioAccent(branding?.accentColor);
    return clearStudioAccent;
  }, [branding?.accentColor]);

  const designs = gallery.data?.flash ?? [];
  const currency = gallery.data?.currency ?? 'gbp';

  return (
    <div className="portal-page" style={{ maxWidth: 960 }}>
      <main className="portal-main">
        <Loader loading={studio.loading} error={studio.error}>
          {branding ? (
            <>
              <PortalHeader studioName={`${branding.name} · Flash`} />
              {branding.logoUrl ? (
                <div style={{ textAlign: 'center', marginTop: -16, marginBottom: 16 }}>
                  <img src={branding.logoUrl} alt="" style={{ maxHeight: 56 }} />
                </div>
              ) : null}
              <p className="portal-lead" style={{ textAlign: 'center', marginBottom: 24 }}>
                Ready-to-tattoo designs. Claim one with a deposit and it&rsquo;s yours.
              </p>

              {params.get('claimed') ? (
                <div className="alert alert-success" style={{ marginBottom: 16 }}>
                  That design is yours. Check your email — your artist will be in touch to agree a date.
                </div>
              ) : null}
              {params.get('cancelled') ? (
                <div className="alert" style={{ marginBottom: 16 }}>
                  Payment was cancelled — the design is still available.
                </div>
              ) : null}
              {!branding.payoutsReady ? (
                <div className="alert" style={{ marginBottom: 16 }}>
                  This studio isn&rsquo;t accepting online deposits yet — check back soon.
                </div>
              ) : null}

              <Loader loading={gallery.loading} error={gallery.error}>
                {designs.length === 0 ? (
                  <div className="portal-panel">
                    <Empty>No flash available right now. Check back soon.</Empty>
                  </div>
                ) : (
                  <div className="gallery">
                    {designs.map((d) => {
                      const gone = goneIds.includes(d.id);
                      return (
                        <div
                          key={d.id}
                          className={`card flash-card${gone ? ' unavailable' : ''}`}
                          style={{ padding: 12, borderRadius: 'var(--radius, 2px)' }}
                        >
                          <img src={d.imageUrl} alt={d.title} loading="lazy" />
                          {gone ? <div className="flash-sold-overlay">SOLD</div> : null}
                          <div className="stack" style={{ marginTop: 10, gap: 6 }}>
                            <strong>{d.title}</strong>
                            {d.description ? <p className="portal-lead">{d.description}</p> : null}
                            <div style={{ fontSize: 15 }}>
                              {formatMoney(d.price, currency)}
                              <span className="portal-lead"> · {formatMoney(d.depositAmount, currency)} deposit</span>
                            </div>
                            <div className="portal-lead">{d.artistName}</div>
                            <Button
                              variant="primary"
                              size="sm"
                              disabled={gone || !branding.payoutsReady}
                              onClick={() => setClaiming(d)}
                            >
                              {gone ? 'Gone' : 'Claim with deposit'}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Loader>

              {claiming ? (
                <ClaimDialog
                  slug={slug}
                  design={claiming}
                  currency={currency}
                  branding={branding}
                  onClose={() => setClaiming(null)}
                  onGone={(id) => {
                    setGoneIds((ids) => [...ids, id]);
                    setClaiming(null);
                  }}
                />
              ) : null}
            </>
          ) : null}
        </Loader>
      </main>
      <PortalFooter />
    </div>
  );
}

/**
 * Name-and-email prompt before handing off to Stripe.
 *
 * @param {{ slug: string, design: object, currency: string,
 *   onClose: () => void, onGone: (id: string) => void }} props
 * @returns {JSX.Element}
 */
function ClaimDialog({ slug, design, currency, branding, onClose, onGone }) {
  const [form, setForm] = useState({ name: '', email: '' });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  // Set when the studio is on embedded payments; the redirect path never does.
  const [payment, setPayment] = useState(null);
  const [paid, setPaid] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post(`/api/public/${slug}/flash/${design.id}/claim`, {
        clientName: form.name,
        clientEmail: form.email,
      });
      // Embedded keeps the client in the gallery; redirect sends them away.
      if (res.clientSecret) {
        setPayment({ clientSecret: res.clientSecret, amount: res.amount });
        setBusy(false);
        return;
      }
      window.location.assign(res.checkoutUrl);
    } catch (err) {
      // Two different 409s are possible here, told apart by message since the
      // API has no machine-readable error code: someone else claimed it
      // between page load and submit (mark it sold), or the studio isn't set
      // up to take deposits (show the error — the "Claim" button is already
      // disabled for that case below, so this is only a defence-in-depth path).
      if (err.status === 409 && !PAYOUTS_NOT_READY_MESSAGE.test(err.message)) {
        onGone(design.id);
        return;
      }
      setError(err.message);
      setBusy(false);
    }
  };

  if (paid) {
    return (
      <Dialog title="Claimed" onClose={onClose}>
        <div className="stack center">
          <h2>&ldquo;{design.title}&rdquo; is yours</h2>
          <p>
            Your deposit is in and the design is off the wall. Check your email — your artist will
            be in touch to agree a date.
          </p>
        </div>
      </Dialog>
    );
  }

  if (payment) {
    return (
      <Dialog title={`Claim "${design.title}"`} onClose={onClose}>
        <div className="stack">
          <p className="portal-terms" style={{ marginTop: 0 }}>
            Paying {formatMoney(design.depositAmount, currency)} now,{' '}
            {formatMoney(design.price - design.depositAmount, currency)} on the day.
          </p>
          <PaymentForm
            publishableKey={branding.stripePublishableKey}
            clientSecret={payment.clientSecret}
            amount={payment.amount}
            currency={currency}
            accentColor={branding.accentColor}
            onSuccess={() => setPaid(true)}
          />
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog title={`Claim "${design.title}"`} onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <ErrorBanner>{error}</ErrorBanner>
        <img
          src={design.imageUrl}
          alt={design.title}
          style={{ maxHeight: 220, objectFit: 'contain', alignSelf: 'center' }}
        />
        <p className="portal-terms" style={{ marginTop: 0 }}>
          {formatMoney(design.depositAmount, currency)} now, {formatMoney(design.price - design.depositAmount, currency)}{' '}
          on the day. Once the deposit clears the design is off the wall and nobody else can take it.
        </p>
        <Input id="clname" label="Name" value={form.name} onChange={set('name')} required autoComplete="name" />
        <Input
          id="clemail"
          label="Email"
          type="email"
          value={form.email}
          onChange={set('email')}
          required
          autoComplete="email"
        />
        <Button type="submit" variant="primary" disabled={busy}>
          {busy ? 'Redirecting…' : `Pay ${formatMoney(design.depositAmount, currency)} deposit`}
        </Button>
      </form>
    </Dialog>
  );
}
